// Mitarbeiter-Tab (design_handoff_team §3): Suche + Archiv-Umschalter (#516), ab 1180 px Glas-Tabelle mit ⋯-Menü,
// darunter Karten. Zeilen-/Kartenklick öffnet die Details.
import { Archive, ArrowCounterClockwise, DotsThree, EnvelopeSimple, Fingerprint, MagnifyingGlass, PencilSimple, Shield, Trash, Users, X } from '@phosphor-icons/react';
import { useState } from 'react';
import { MenueEintrag, Popover } from '../../../../components/immobilien/ui';
import { istArchiviert } from '../../../../lib/team/archiv';
import { kurzId, rollenLabel, statusText, zaehlerText, type TeamMitglied } from '../../../../lib/team/mitarbeiter';
import { FehlerKarte, LadeKarte, LeerKarte, MitarbeiterAvatar, RollenChip, StatusChip, rollenStil, statusFarbe } from './Bausteine';

export interface MitarbeiterListeProps {
  liste: TeamMitglied[];
  laedt: boolean;
  fehler: string | null;
  onErneut: () => void;
  suche: string;
  onSuche: (s: string) => void;
  zeigeArchiv: boolean;
  onZeigeArchiv: () => void;
  anzahlArchiv: number;
  tabelle: boolean;
  mobil: boolean;
  onOeffnen: (m: TeamMitglied) => void;
  onBearbeiten: (m: TeamMitglied) => void;
  onLoeschen: (m: TeamMitglied) => void;
  onArchiv: (m: TeamMitglied, an: boolean) => void;
}

const SPALTEN = 'minmax(220px,2.2fr) minmax(120px,1fr) minmax(120px,1fr) minmax(100px,0.9fr) 52px';

export function MitarbeiterListe(p: MitarbeiterListeProps) {
  const { liste, laedt, fehler, suche, tabelle, mobil } = p;
  const [suchFokus, setSuchFokus] = useState(false);

  return (
    <div className="flex flex-col" style={{ gap: 14 }}>
      <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
        <label data-tip="Suchen – Nach Name oder E-Mail filtern" className="immo-glas flex items-center"
          onFocus={() => setSuchFokus(true)} onBlur={() => setSuchFokus(false)}
          style={{ flex: '1 1 280px', maxWidth: mobil ? undefined : 460, minWidth: 0, height: 44, borderRadius: 22, gap: 10, padding: '0 16px',
            ...(suchFokus ? { boxShadow: 'var(--glass-sh), 0 0 0 2px var(--acc)' } : {}) }}>
          <MagnifyingGlass size={16} weight="bold" color="var(--ink3)" aria-hidden />
          <input value={suche} onChange={(e) => p.onSuche(e.target.value)} placeholder="Teammitglied suchen..." aria-label="Teammitglied suchen"
            className="min-w-0 flex-1" style={{ height: '100%', border: 'none', outline: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, color: 'var(--ink)' }} />
          {suche && (
            <button type="button" data-tip="Suche leeren" aria-label="Suche leeren" onClick={() => p.onSuche('')}
              className="flex items-center justify-center" style={{ width: mobil ? 32 : 24, height: mobil ? 32 : 24, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'var(--fill3)', color: 'var(--ink)' }}>
              <X size={11} weight="bold" />
            </button>
          )}
        </label>
        <button type="button" onClick={p.onZeigeArchiv} aria-pressed={p.zeigeArchiv}
          data-tip={p.zeigeArchiv ? 'Aktive anzeigen – Zurück zu den aktiven Mitarbeitern' : 'Archiv – Archivierte Mitarbeiter anzeigen'}
          className={p.zeigeArchiv ? 'inline-flex items-center' : 'immo-glas inline-flex items-center'}
          style={{ height: 44, padding: '0 16px', gap: 8, borderRadius: 22, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 800,
            ...(p.zeigeArchiv ? { background: 'var(--acc-soft)', color: 'var(--acc)' } : { color: 'var(--ink)' }) }}>
          <Archive size={16} weight={p.zeigeArchiv ? 'fill' : 'bold'} color="var(--acc)" />
          {p.zeigeArchiv ? 'Aktive anzeigen' : `Archiv (${p.anzahlArchiv})`}
        </button>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink3)' }}>{zaehlerText(liste.length, !!suche)}</span>
      </div>

      {laedt ? (
        <LadeKarte text="Lade Teammitglieder..." />
      ) : fehler ? (
        <FehlerKarte meldung={fehler} onErneut={p.onErneut} />
      ) : liste.length === 0 ? (
        <LeerKarte icon={Users} titel="Keine Mitarbeiter unter diesen Kriterien gefunden">
          <button type="button" onClick={() => p.onSuche('')} data-tip="Filter löschen – Suche zurücksetzen"
            style={{ marginTop: 4, height: 40, padding: '0 18px', border: 'none', borderRadius: 20, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 800, background: 'var(--acc-soft)', color: 'var(--acc)' }}>
            Filter löschen
          </button>
        </LeerKarte>
      ) : tabelle ? (
        <Tabelle {...p} />
      ) : (
        <Karten {...p} />
      )}
    </div>
  );
}

function AktionsMenue({ m, onBearbeiten, onLoeschen, onArchiv }: Pick<MitarbeiterListeProps, 'onBearbeiten' | 'onLoeschen' | 'onArchiv'> & { m: TeamMitglied }) {
  const archiviert = istArchiviert(m);
  return (
    <Popover label={`Aktionen für ${m.full_name || 'Mitarbeiter'}`} breite={210}
      ausloeser={({ offen, umschalten, ref }) => (
        <button ref={ref} type="button" aria-haspopup="menu" aria-expanded={offen} aria-label="Aktionen" data-tip="Aktionen – Bearbeiten oder löschen"
          onClick={(e) => { e.stopPropagation(); umschalten(); }}
          className="immo-hover-flaeche flex items-center justify-center"
          style={{ width: 36, height: 36, borderRadius: '50%', border: 'none', cursor: 'pointer', background: offen ? 'var(--fill3)' : 'transparent', color: 'var(--ink)' }}>
          <DotsThree size={18} weight="bold" />
        </button>
      )}>
      {(zu) => (
        // Portal-Klicks steigen im React-Baum bis zur Zeile auf – dort würde sonst die Detailseite aufgehen.
        <div className="flex flex-col" onClick={(e) => e.stopPropagation()}>
          <MenueEintrag onClick={() => { zu(); onBearbeiten(m); }}><PencilSimple size={16} weight="bold" color="var(--acc)" />Bearbeiten</MenueEintrag>
          <MenueEintrag onClick={() => { zu(); onArchiv(m, !archiviert); }}>
            {archiviert ? <ArrowCounterClockwise size={16} weight="bold" color="var(--acc)" /> : <Archive size={16} weight="bold" color="var(--acc)" />}
            {archiviert ? 'Wiederherstellen' : 'Archivieren'}
          </MenueEintrag>
          <span aria-hidden style={{ height: 1, margin: '4px 8px', background: 'var(--line)' }} />
          <MenueEintrag gefahr onClick={() => { zu(); onLoeschen(m); }}><Trash size={16} weight="bold" />Löschen</MenueEintrag>
        </div>
      )}
    </Popover>
  );
}

function Tabelle(p: MitarbeiterListeProps) {
  return (
    <div role="table" aria-label="Teammitglieder" className="immo-glas flex flex-col" style={{ borderRadius: 26, padding: 6 }}>
      <div role="row" className="grid" style={{ gridTemplateColumns: SPALTEN, gap: 12, padding: '10px 16px', fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink3)' }}>
        <span role="columnheader">Name &amp; E-Mail</span><span role="columnheader">Rolle</span><span role="columnheader">Sicherheit</span>
        <span role="columnheader">Status</span><span role="columnheader" style={{ textAlign: 'right' }}>Aktionen</span>
      </div>
      {p.liste.map((m) => (
        <div key={m.id} role="row" tabIndex={0} data-tip={`${m.full_name || 'Unbekannt'} – Details und Berechtigungen öffnen`}
          onClick={() => p.onOeffnen(m)} onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) p.onOeffnen(m); }}
          className="immo-zeile-hover grid items-center"
          style={{ gridTemplateColumns: SPALTEN, gap: 12, padding: '10px 16px', borderRadius: 20, cursor: 'pointer' }}>
          <div role="cell" className="flex min-w-0 items-center" style={{ gap: 12 }}>
            <MitarbeiterAvatar id={m.id} name={m.full_name} url={m.avatar_url} groesse={42} />
            <div className="flex min-w-0 flex-col">
              <span className="truncate" style={{ fontSize: 14.5, fontWeight: 800 }}>{m.full_name || 'Unbekannt'}</span>
              <span className="truncate" style={{ fontSize: 12.5, color: 'var(--ink3)' }}>{m.email}</span>
            </div>
          </div>
          <span role="cell"><RollenChip role={m.role} /></span>
          <span role="cell" data-tip="Sicherheit – Zwei-Faktor-Anmeldung aktiv" className="inline-flex items-center justify-self-start" style={{ gap: 6, fontSize: 12, fontWeight: 800, color: 'var(--ok)' }}>
            <Fingerprint size={16} weight="fill" />2FA aktiv
          </span>
          <span role="cell"><StatusChip status={m.status} punkt /></span>
          <div role="cell" className="flex justify-end">
            <AktionsMenue m={m} onBearbeiten={p.onBearbeiten} onLoeschen={p.onLoeschen} onArchiv={p.onArchiv} />
          </div>
        </div>
      ))}
    </div>
  );
}

function Kachel({ icon: I, label, wert, fg, bg }: { icon: typeof Shield; label: string; wert: string; fg: string; bg: string }) {
  return (
    <div className="flex min-w-0 items-center" style={{ borderRadius: 16, padding: '10px 12px', gap: 10, background: 'var(--fill)' }}>
      <span className="flex shrink-0 items-center justify-center" style={{ width: 30, height: 30, borderRadius: 10, background: bg }}>
        <I size={15} weight="fill" color={fg} />
      </span>
      <div className="flex min-w-0 flex-col">
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink3)' }}>{label}</span>
        <span className="truncate" style={{ fontSize: 13, fontWeight: 800 }}>{wert}</span>
      </div>
    </div>
  );
}

function Karten(p: MitarbeiterListeProps) {
  const knopf = { width: 44, height: 44, border: 'none', borderRadius: 14, cursor: 'pointer' } as const;
  return (
    <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))', gap: 12 }}>
      {p.liste.map((m) => {
        const rs = rollenStil(m.role);
        const archiviert = istArchiviert(m);
        return (
          <article key={m.id} className="immo-glas flex min-w-0 flex-col" style={{ borderRadius: 26, padding: 16, gap: 14 }}>
            <button type="button" onClick={() => p.onOeffnen(m)} data-tip={`${m.full_name || 'Unbekannt'} – Details und Berechtigungen öffnen`}
              className="flex min-w-0 items-center text-left" style={{ gap: 12, border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', color: 'inherit', fontFamily: 'inherit' }}>
              <MitarbeiterAvatar id={m.id} name={m.full_name} url={m.avatar_url} groesse={50} punkt={statusFarbe(m.status).fg} />
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate" style={{ fontSize: 16, fontWeight: 800 }}>{m.full_name || 'Unbekannt'}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)', fontFamily: 'ui-monospace, Menlo, monospace' }}>#{kurzId(m.id)}</span>
              </div>
              <span className="shrink-0" style={{ height: 26, padding: '0 10px', borderRadius: 13, display: 'inline-flex', alignItems: 'center', fontSize: 12, fontWeight: 800,
                background: statusFarbe(m.status).bg, color: statusFarbe(m.status).fg }}>{statusText(m.status)}</span>
            </button>
            <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <Kachel icon={Shield} label="Rolle" wert={rollenLabel(m.role, false)} fg={rs.fg} bg={rs.bg} />
              <Kachel icon={Fingerprint} label="Sicherheit" wert="2FA aktiv" fg="var(--ok)" bg="var(--ok-soft)" />
            </div>
            <div className="flex" style={{ gap: 8 }}>
              <a href={`mailto:${m.email}`} data-tip="E-Mail schreiben" className="flex flex-1 items-center justify-center"
                style={{ height: 44, borderRadius: 14, gap: 7, fontSize: 13, fontWeight: 800, background: 'var(--fill2)', color: 'var(--ink)', textDecoration: 'none' }}>
                <EnvelopeSimple size={16} weight="fill" color="var(--info)" />E-Mail
              </a>
              <button type="button" data-tip="Bearbeiten" aria-label="Bearbeiten" onClick={() => p.onBearbeiten(m)}
                className="flex items-center justify-center" style={{ ...knopf, background: 'var(--acc-soft)', color: 'var(--acc)' }}>
                <PencilSimple size={17} weight="bold" />
              </button>
              <button type="button" data-tip={archiviert ? 'Wiederherstellen' : 'Archivieren'} aria-label={archiviert ? 'Wiederherstellen' : 'Archivieren'}
                onClick={() => p.onArchiv(m, !archiviert)}
                className="flex items-center justify-center" style={{ ...knopf, background: 'var(--fill2)', color: 'var(--ink2)' }}>
                {archiviert ? <ArrowCounterClockwise size={17} weight="bold" /> : <Archive size={17} weight="bold" />}
              </button>
              <button type="button" data-tip="Löschen" aria-label="Löschen" onClick={() => p.onLoeschen(m)}
                className="flex items-center justify-center" style={{ ...knopf, background: 'var(--bad-soft)', color: 'var(--bad)' }}>
                <Trash size={17} weight="bold" />
              </button>
            </div>
          </article>
        );
      })}
    </div>
  );
}
